import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { AxiosProvider, WfAuth0ProviderFromViteConfig, WfAuthnProvider } from "auth-sdk";
import { BlackBox, type DatadogInitConfig } from "@wayflyer/blackbox-fe";
import { AppShellController, FlyUIProvider, NavigationProvider } from "@wayflyer/flyui";
import {
  AppErrorModalProvider,
  BankConnectConsentProvider,
  CompanyInactiveModal,
  ConnectorProvider,
  ErrorComponent,
  getCustomerErrorPresetMap,
  InviteModalRenderer,
  InviteProvider,
  useFindViewOwner,
  usePostHogPageView,
} from "shared-ui";
import { queryClient, AnalyticsProvider } from "shared-utils";
import { BaseSchemas } from "api-types";
import type { BankConnectScreenProps } from "funding-application";
import { bankConnectScreenPropsOptions } from "funding-application";
import { memo, Suspense, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { IntlProvider, useIntl } from "react-intl";
import { RouterProvider } from "react-router-dom";
import { useCustomerAppConfig } from "undercarriage";

import { router } from "../../router";
import { getAnalyticsAttributes } from "../analytics/get-analytics-attributes";
import { usePostHogIdentify } from "../analytics/posthog";
import { collectPrivacyPolicyConsent } from "../consent";
import { ConsentContextProvider } from "../context-providers/ConsentContext";
import { convertNumDaysToDateRange, getNumDaysSinceDate } from "../utils/date-helpers";
import { Fallback } from "../../fallback";

import { useIntercomLoaded } from "./intercom";
import { useNavigationItems } from "./navigation/use-navigation-items";

import "@wayflyer/flyui/styles.css";

const messagesCache = {};

// Acholi is a magic language for enabling the Crowdin in context editor
// It is a real language, so we might one day need to change, but I doubt it
// See https://support.crowdin.com/enterprise/in-context/
const getLocale = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (urlSearchParams.get("crowdin_editor") === "true") {
    return "ach";
  }
  return navigator.language === "*" ? "en" : navigator.language;
};

const getDatadogUserContext = (appConfig: BaseSchemas.CustomerAppConfig): DatadogInitConfig["user"] | undefined => {
  const analyticsAttributes = getAnalyticsAttributes(appConfig);
  const { userName, userEmail, customerName } = appConfig;
  if (!("userId" in analyticsAttributes) || analyticsAttributes.userId == null) {
    return undefined;
  }
  const lastLoginInDays = analyticsAttributes.lastLogin && getNumDaysSinceDate(analyticsAttributes.lastLogin);
  const lastLoginDateRange = lastLoginInDays && convertNumDaysToDateRange(lastLoginInDays);
  const deviceResolution = `${window.screen.width}x${window.screen.height}`;
  const isValidSessionRedirect = !!sessionStorage.getItem("validSessionRedirect");
  return {
    id: String(analyticsAttributes.userId),
    name: userName,
    isStaff: analyticsAttributes.isStaff,
    lastLogin: analyticsAttributes.lastLogin,
    lastLoginInDays,
    lastLoginDateRange,
    createdAt: analyticsAttributes.createdAt,
    customerName,
    deviceResolution,
    validSessionRedirect: isValidSessionRedirect,
    email: userEmail,
    isInternalUser: userEmail?.endsWith("@wayflyer.com"),
    ...analyticsAttributes,
  };
};

function getMessages(locale) {
  if (messagesCache[locale]) {
    return messagesCache[locale];
  }
  throw loadMessages(locale);
}

async function loadMessages(locale) {
  let messages;
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (locale.startsWith("es")) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/es.json");
  } else if (locale.startsWith("de")) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/de.json");
  } else if (urlSearchParams.get("crowdin_editor") === "true") {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/ach.json");
  } else {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS2307
    messages = await import("../../../compiled-lang/en.json");
  }

  messagesCache[locale] = messages.default;
  return messages.default;
}

const AsyncIntlProvider = ({ children }) => {
  const locale = getLocale();
  const messages = getMessages(locale);

  return (
    <IntlProvider locale={locale} messages={messages} defaultLocale="en" onError={() => {}}>
      {children}
    </IntlProvider>
  );
};

const NavigationWithInviteContext = memo(
  ({
    appConfig,
    bankConnectPropsData,
    children,
  }: {
    appConfig: BaseSchemas.CustomerAppConfig;
    bankConnectPropsData: BankConnectScreenProps;
    children: React.ReactNode;
  }) => {
    const navigationProps = useNavigationItems({ appConfig });

    const consentHandler = useCallback(() => {
      return collectPrivacyPolicyConsent(appConfig.homePath);
    }, [appConfig.homePath]);

    return (
      <NavigationProvider {...navigationProps}>
        <InviteModalRenderer />
        <CompanyInactiveModal isOpen={!!appConfig.customerInactive} />
        <ConnectorProvider>
          <BankConnectConsentProvider
            consentNeeded={Boolean(bankConnectPropsData?.bankConnectProps.consentNeeded)}
            consentHandler={consentHandler}
          >
            <ConsentContextProvider consentHandler={consentHandler}>{children}</ConsentContextProvider>
          </BankConnectConsentProvider>
        </ConnectorProvider>
      </NavigationProvider>
    );
  }
);

NavigationWithInviteContext.displayName = "NavigationWithInviteContext";

export const RootContainer = ({ children }: { children: React.ReactNode }) => {
  const { data: appConfig } = useCustomerAppConfig();
  const analyticsAttributes = getAnalyticsAttributes(appConfig);
  useIntercomLoaded();
  useFindViewOwner();
  const intl = useIntl();
  const { data: bankConnectPropsData } = useQuery({
    ...bankConnectScreenPropsOptions(appConfig.customerDomain),
    enabled: !!appConfig.customerDomain,
  });
  usePostHogIdentify({ ...appConfig, analyticsAttributes });
  usePostHogPageView();

  return (
    <BlackBox FallbackComponent={ErrorFallback} user={getDatadogUserContext(appConfig)}>
      <AnalyticsProvider value={analyticsAttributes}>
        <InviteProvider homePath={appConfig.homePath}>
          <FlyUIProvider intl={intl} theme="wayflyer" defaultLinkBehavior="clientSideNav">
            <AppErrorModalProvider>
              <NavigationWithInviteContext appConfig={appConfig} bankConnectPropsData={bankConnectPropsData}>
                <AppShellController>{children}</AppShellController>
              </NavigationWithInviteContext>
            </AppErrorModalProvider>
          </FlyUIProvider>
        </InviteProvider>
      </AnalyticsProvider>
    </BlackBox>
  );
};

const getRootNode = () => {
  const rootNode = document.getElementById("app-container");
  if (!rootNode) {
    throw new Error("Root node not found");
  }
  return createRoot(rootNode);
};

const ErrorFallback = ({ resetErrorBoundary, variant }) => {
  const intl = useIntl();
  const { title, message, primaryAction } = getCustomerErrorPresetMap({ intl })[variant];
  return (
    <ErrorPage>
      <ErrorComponent
        {...{
          title,
          message,
          primaryAction,
          secondaryAction: {
            content: intl.formatMessage({ id: "error.goBack", defaultMessage: "Go back" }),
            onAction: resetErrorBoundary,
          },
        }}
      />
    </ErrorPage>
  );
};

export function RootComponent({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<Fallback />}>
      <QueryClientProvider client={queryClient}>
        <AxiosProvider>
          <AppTree>{children}</AppTree>
        </AxiosProvider>
      </QueryClientProvider>
    </Suspense>
  );
}

export function renderRootComponent() {
  const root = getRootNode();
  root.render(<RouterProvider router={router} />);
}

export function AppTree({ children }: { children: React.ReactNode }) {
  return (
    <AsyncIntlProvider>
      <WfAuth0ProviderFromViteConfig>
        <WfAuthnProvider>
          <RootContainer>{children}</RootContainer>
        </WfAuthnProvider>
      </WfAuth0ProviderFromViteConfig>
    </AsyncIntlProvider>
  );
}

// We want to keep the rendering of our error components as simple as possible to avoid
// the Error components throwing errors themselves. Avoid wrapping/mounting with unnecessary providers.
export function ErrorPage({ children }: { children: React.ReactNode }) {
  return (
    <AxiosProvider>
      <QueryClientProvider client={queryClient}>
        <AsyncIntlProvider>
          <FlyUIProvider theme="wayflyer">{children}</FlyUIProvider>
        </AsyncIntlProvider>
      </QueryClientProvider>
    </AxiosProvider>
  );
}
